/**
 * Protected Route Component
 *
 * Wrapper component that ensures the user is authenticated before rendering children.
 * Shows loading state during initialization and redirects to login if not authenticated.
 */

import { useAuthStore } from "@/store/authStore";

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function ProtectedRoute({ children, fallback }: ProtectedRouteProps) {
  const { user, isInitialized, isLoading } = useAuthStore();

  // Don't handle redirects here - let StoreInitializer handle them
  // This component only protects content rendering

  // Show loading state while initializing
  if (!isInitialized || isLoading) {
    return (
      fallback || (
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
            <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
          </div>
        </div>
      )
    );
  }

  // After initialization completes, don't render children if user is not authenticated
  // StoreInitializer will handle the redirect
  if (!user) {
    return null;
  }

  return <>{children}</>;
}
